import { BellRinging } from '@phosphor-icons/react';
import { toMinutes } from '@shh/shared';
import { Icon } from '@shh/ui';
import { useEffect } from 'react';
import { eventsOn, participants, type E } from '../data/derive';
import { useFamily } from '../data/family';
import { getGong, playGong, primeGong } from '../lib/gong';
import { calendarText } from '../i18n/calendar';

/** How long before an event the wall shows its reminder card (and sounds the gong). */
export const WALL_LEAD = 15;

/** The event starting within the next 15 minutes, if any (Erinnerungen am Wanddisplay, 1.13). */
export function useUpcoming(now: string): { e: E; minutes: number } | null {
  const f = useFamily();
  const t = toMinutes(now);
  for (const e of eventsOn(f, f.today)) {
    if (!e.start || e.virtual || e.remind === 'none') continue;
    const minutes = toMinutes(e.start) - t;
    if (minutes > 0 && minutes <= WALL_LEAD) return { e, minutes };
  }
  return null;
}

/**
 * The gong sounds once per event on this device – only on the wall, only if this device wants
 * it (Einstellungen → Dieses Gerät), never in the night rest.
 */
export function useWallGong(upcoming: { e: E } | null, enabled: boolean) {
  const f = useFamily();
  useEffect(() => {
    if (enabled) primeGong();
  }, [enabled]);
  useEffect(() => {
    if (!enabled || !upcoming || getGong() === 'off') return;
    const key = `shh.gong.${upcoming.e.id}.${f.today}`;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
    } catch {
      /* without storage it may sound again after a reload – acceptable */
    }
    playGong();
  }, [enabled, upcoming, f.today]);
}

/** The reminder card on "Heute" at the wall (awake). */
export function WallReminder({ upcoming }: { upcoming: { e: E; minutes: number } }) {
  const f = useFamily();
  const { e, minutes } = upcoming;
  const who =
    e.assignees === 'all'
      ? ''
      : participants(f, e)
          .map((m) => m.name)
          .join(', ');
  const extra = [who, e.transport, e.bring?.length ? calendarText.bring(e.bring.join(', ')) : '']
    .filter(Boolean)
    .join(' · ');
  return (
    <section
      aria-label={calendarText.reminder}
      role="status"
      className="flex items-start gap-gap-md rounded-card border-2 border-accent-primary bg-accent-secondary-soft p-inset-md"
    >
      <Icon
        icon={BellRinging}
        size="lg"
        weight="duotone"
        className="shrink-0 text-accent-primary"
      />
      <div className="flex flex-col gap-gap-xs">
        <span className="text-sm font-bold uppercase tracking-wide text-accent-primary">
          {calendarText.inMinutesCap(minutes)} · {e.start}
        </span>
        <span className="font-display text-2xl font-black">{e.title}</span>
        {extra && <span className="text-text-secondary">{extra}</span>}
      </div>
    </section>
  );
}
